import { IonContent, IonPage } from "@ionic/react";
import { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { DayMarksSheet } from "../components/DayMarks";
import { Hero, Over } from "../components/Hero";
import { Insight } from "../components/Insight";
import { I } from "../components/icons";
import { DiscloseRow, NavRow, RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { addDays, dayLabel, parseDay, todayStr } from "../domain/date";
import { foodWeek, recentWeeks, weekCountLine, weekDayLine, weekInsights, weekOf, weekRange, type WeekDay } from "../domain/foodWeek";
import { dayLedger } from "../domain/ledger";
import { useKaya } from "../store/kayaStore";
import "./Progress.css";

const n = (v: number) => Math.round(v).toLocaleString("en-US");

/**
 * /progress/food/week — Progress › Food by week, on complete days only (board ProgressFoodWeek;
 * ADR-049). The answer is the week's average on the days logged in full; the seven days say which
 * counted and why the others did not; the sentences say what the week shows; the other weeks of
 * the last 28 days are a tap away. Like the rest of Progress › Food it reads to yesterday.
 *
 * A day row opens that day's marks, so a day already past — the dinner not logged, the fast
 * kept — can be marked where its absence from the average is seen. The day sheet in Food reaches
 * today and the week ahead only.
 */
export default function ProgressFoodWeek() {
  const S = useKaya((s) => s.S);
  const location = useLocation();
  const today = todayStr();
  const end = addDays(today, -1);
  const asked = new URLSearchParams(location.search).get("w");
  const [monday, setMonday] = useState(() =>
    asked && /^\d{4}-\d{2}-\d{2}$/.test(asked) ? weekOf(asked) : weekOf(end)
  );
  const [marking, setMarking] = useState<string | null>(null);
  const content = useRef<HTMLIonContentElement>(null);

  const budget = (ds: string) => dayLedger(S, ds, parseDay(ds)).budget;
  const w = foodWeek(S, monday, end, budget);
  const thisWeek = weekOf(end);
  const others = recentWeeks(S, end, budget).filter((x) => x.from !== w.from);
  const title = w.from === thisWeek ? "This week" : `Week of ${dayLabel(w.from)}`;

  const line = [
    `${w.complete} complete ${w.complete === 1 ? "day" : "days"} of ${w.days.length}`,
    w.budget !== null ? `budget ~${n(w.budget)} est.` : null,
    w.judged ? (w.within ? `within it on ${w.within}` : `over it on ${w.judged === 1 ? "the one" : `all ${w.judged}`}`) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const value = (d: WeekDay) => {
    if (d.mark) return "left out";
    if (!d.complete || d.kcal === null) return "not logged";
    if (d.over !== null)
      return (
        <>
          {n(d.kcal)} · <Over>{n(d.over)} over</Over>
        </>
      );
    return n(d.kcal);
  };

  return (
    <IonPage>
      <IonContent ref={content}>
        <Screen>
          <TopBar title="Food by week" back={{ to: "/progress/food", label: "Back to Food" }} />
          <div className="pg">
            <Hero
              label={`${title} · complete days`}
              figure={w.avgKcal}
              unit="kcal a day"
              line={w.avgKcal === null ? "No complete day yet — nothing is counted as zero" : line}
              sub={
                w.avgProtein !== null
                  ? `protein ${n(w.avgProtein)}${w.proteinTarget !== null ? ` of ${n(w.proteinTarget)}` : ""} g a day`
                  : undefined
              }
            />

            <RowGroup heading={w.days.length === 7 ? "The seven days" : "The days so far"}>
              {w.days.map((d) => (
                <NavRow
                  key={d.ds}
                  title={
                    <>
                      {dayLabel(d.ds)}
                      {d.mark === "fast" ? (
                        <>
                          {" "}
                          <Tag tone="fast" />
                        </>
                      ) : d.mark === "untracked" ? (
                        <>
                          {" "}
                          <Tag tone="untracked" />
                        </>
                      ) : null}
                    </>
                  }
                  subtitle={weekDayLine(d) ?? undefined}
                  value={value(d)}
                  onClick={() => setMarking(d.ds)}
                />
              ))}
            </RowGroup>

            <RowGroup heading="What the week says">
              {weekInsights(w).map((i, k) => (
                <Insight key={i.key} insight={i} first={k === 0} />
              ))}
            </RowGroup>

            {others.length ? (
              <RowGroup heading="Other weeks · last 28 days">
                {others.map((x) => (
                  <NavRow
                    key={x.from}
                    title={x.from === thisWeek ? "This week" : weekRange(x)}
                    subtitle={
                      x.from === thisWeek ? `${weekRange(x, true)} · ${weekCountLine(x)}` : weekCountLine(x)
                    }
                    value={x.avgKcal === null ? "–" : n(x.avgKcal)}
                    onClick={() => {
                      setMonday(x.from);
                      void content.current?.scrollToTop(200);
                    }}
                  />
                ))}
              </RowGroup>
            ) : null}

            <RowGroup>
              <DiscloseRow icon={I.info} title="How days count" defaultOpen>
                <div className="pg-disclose">
                  <p>
                    A day counts when food is logged and it isn&rsquo;t marked untracked. A fast day is kept out of
                    the averages and is never a missed day. A day with nothing logged is left out, not counted as zero.
                  </p>
                  <p>
                    Mark a day untracked or a fast day from its day sheet in <Link to="/food/plan">Food</Link>, or
                    tap a day above.
                  </p>
                </div>
              </DiscloseRow>
            </RowGroup>
          </div>
        </Screen>
        {marking ? <DayMarksSheet ds={marking} onClose={() => setMarking(null)} /> : null}
      </IonContent>
    </IonPage>
  );
}
